import { useState } from "react";
import { LoaderCircle } from "lucide-react";
import { useRouter } from "@shared/client/navigation";
import type { AdminUserRecord } from "@shared/lib/admin-users";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@shared/components/ui/dialog";

/** Confirmation de suppression : dit ce qui part avec le compte, et pourquoi c'est parfois refusé. */
export function UserDeleteDialog({
  user,
  currentUserId,
  onClose,
}: {
  user: AdminUserRecord | null;
  currentUserId: string | null;
  onClose: () => void;
}) {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const blocked = !user
    ? null
    : user.id === currentUserId
      ? "Vous ne pouvez pas supprimer votre propre compte ici."
      : user.paying
        ? "Cet utilisateur a un abonnement Stripe en cours : résiliez-le d’abord dans Stripe, puis revenez le supprimer."
        : null;

  function close() {
    setError("");
    onClose();
  }

  async function remove() {
    if (!user) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`/api/admin/users/${user.id}`, { method: "DELETE" });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Suppression impossible.");
      router.refresh();
      close();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Suppression impossible.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog onOpenChange={(open) => !open && close()} open={user !== null}>
      <DialogContent className="sm:max-w-md">
        {user && (
          <>
            <DialogHeader className="text-left">
              <DialogTitle>Supprimer {user.full_name.trim() || user.email} ?</DialogTitle>
              <DialogDescription>
                Le compte {user.email} disparaît avec ses {user.offers} offre{user.offers > 1 ? "s" : ""}, ses passages, ses
                leads et ses sessions. Ses traces IA restent, sans compte. C’est définitif.
              </DialogDescription>
            </DialogHeader>
            {(blocked || error) && (
              <Alert role="alert" variant="destructive">
                <AlertDescription>{blocked ?? error}</AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button onClick={close} variant="outline">Annuler</Button>
              <Button disabled={busy || blocked !== null} onClick={remove} variant="destructive">
                {busy && <LoaderCircle className="animate-spin" />} Supprimer le compte
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
